import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import {
  cancelCompanionRequest,
  searchCompanions,
  sendCompanionRequest,
} from '../../api/companions';
import { useToast } from '../tavern/ToastProvider';

const getInviteLink = () => `${window.location.origin}/register`;

const InviteCompanionSheet = ({ onClose, open }) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const inviteLink = getInviteLink();
  const shareTitle = t('friends.sheet.shareTitle');
  const shareText = t('friends.sheet.shareText', { url: inviteLink });

  useEffect(() => {
    let mounted = true;
    const cleanedQuery = query.trim();

    if (cleanedQuery.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const timeoutId = window.setTimeout(() => {
      searchCompanions({ query: cleanedQuery })
        .then((data) => {
          if (mounted) setResults(data.items || []);
        })
        .catch(() => {
          if (mounted) toast.error(t('friends.sheet.searchError'));
        })
        .finally(() => {
          if (mounted) setSearching(false);
        });
    }, 250);

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [query, t, toast]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(shareText);
      toast.success(t('friends.sheet.linkCopied'));
    } finally {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    await handleCopy();
  };

  const handleSendRequest = async (userId) => {
    setSendingId(userId);
    try {
      const response = await sendCompanionRequest(userId);
      const status = response.status || 'pending';
      setResults((current) =>
        current.map((result) =>
          result.id === userId
            ? {
                ...result,
                friendship: {
                  ...(result.friendship || {}),
                  id: response.id || result.friendship?.id || null,
                  status,
                  direction: status === 'accepted' ? 'incoming' : 'outgoing',
                },
              }
            : result
        )
      );
      toast.success(
        status === 'accepted'
          ? t('friends.sheet.acceptedFeedback')
          : t('friends.sheet.requestSent')
      );
    } catch (error) {
      const status = error?.response?.status;
      toast.error(
        status === 409
          ? t('friends.sheet.requestAlreadyExists')
          : t('friends.sheet.requestError')
      );
    } finally {
      setSendingId(null);
    }
  };

  const handleCancelRequest = async (result) => {
    const requestId = result.friendship?.id;
    if (!requestId) return;

    setCancelingId(result.id);
    try {
      await cancelCompanionRequest(requestId);
      setResults((current) =>
        current.map((item) =>
          item.id === result.id
            ? {
                ...item,
                friendship: { id: null, status: 'none', direction: 'none' },
              }
            : item
        )
      );
      toast.success(t('friends.sheet.canceledFeedback'));
    } catch {
      toast.error(t('friends.sheet.cancelError'));
    } finally {
      setCancelingId(null);
    }
  };

  const actionLabel = (friendship) => {
    if (friendship?.status === 'accepted') return t('friends.sheet.alreadyCompanion');
    if (friendship?.status === 'pending' && friendship.direction === 'outgoing') {
      return t('friends.sheet.pending');
    }
    if (friendship?.status === 'pending' && friendship.direction === 'incoming') {
      return t('friends.sheet.acceptRequest');
    }
    return t('friends.sheet.sendInvite');
  };

  const actionDisabled = (result) =>
    sendingId === result.id ||
    result.friendship?.status === 'accepted';

  const renderAction = (result) => {
    if (
      result.friendship?.status === 'pending' &&
      result.friendship.direction === 'outgoing'
    ) {
      return (
        <Button
          size="small"
          variant="outlined"
          disabled={cancelingId === result.id}
          onClick={() => handleCancelRequest(result)}
        >
          {cancelingId === result.id ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            t('friends.sheet.cancelRequest')
          )}
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant={result.friendship?.status === 'accepted' ? 'outlined' : 'contained'}
        disabled={actionDisabled(result)}
        onClick={() => handleSendRequest(result.id)}
      >
        {sendingId === result.id ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          actionLabel(result.friendship)
        )}
      </Button>
    );
  };

  return (
    <Drawer
      anchor={desktop ? 'right' : 'bottom'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { md: 440 },
          maxHeight: { xs: '90vh', md: '100vh' },
          borderTopLeftRadius: { xs: 24, md: 24 },
          borderTopRightRadius: { xs: 24, md: 0 },
          bgcolor: 'background.default',
        },
      }}
    >
      <Box sx={{ p: 3.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{ position: 'absolute', top: 16, right: 16 }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            mb: 2,
            color: 'primary.contrastText',
            background: 'linear-gradient(135deg, #0F4C45, #0B3A35)',
            boxShadow: 2,
          }}
        >
          <AutoAwesomeIcon />
        </Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
          {t('friends.sheet.eyebrow')}
        </Typography>
        <Typography variant="h3">{t('friends.sheet.title')}</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {t('friends.sheet.subtitle')}
        </Typography>
      </Box>

      <Stack spacing={3} sx={{ p: 3.5 }}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('friends.sheet.findOnTavern')}
          </Typography>
          <TextField
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('friends.sheet.searchPlaceholder')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          {searching ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={18} />
              <Typography color="text.secondary">{t('friends.sheet.searching')}</Typography>
            </Stack>
          ) : null}
          {query.trim().length >= 2 && !searching && results.length === 0 ? (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="h4">{t('friends.sheet.noSearchResults')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('friends.sheet.noSearchResultsHint')}
                </Typography>
              </CardContent>
            </Card>
          ) : null}
          {results.length > 0 ? (
            <Stack spacing={1.25} sx={{ mt: 2 }}>
              {results.map((result) => (
                <Card key={result.id} variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={result.avatarUrl || undefined}>
                        {result.displayName?.[0] || result.username?.[0]}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h4" noWrap>
                          {result.displayName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          @{result.username}
                        </Typography>
                      </Box>
                      {result.friendship?.status === 'pending' ? (
                        <Chip
                          size="small"
                          label={
                            result.friendship.direction === 'incoming'
                              ? t('friends.sheet.incoming')
                              : t('friends.sheet.pending')
                          }
                        />
                      ) : null}
                      {renderAction(result)}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : null}
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('friends.sheet.invitationLink')}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              border: '2px dashed',
              borderColor: 'secondary.light',
              borderRadius: 3,
              p: 2,
              pr: 7,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h4" color="primary.main" noWrap>
              {inviteLink}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {t('friends.sheet.expiresIn', { count: 7 })}
            </Typography>
            <IconButton
              onClick={handleCopy}
              aria-label={t('friends.sheet.copyLink')}
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: copied ? 'success.main' : 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: copied ? 'success.dark' : 'primary.dark' },
              }}
            >
              {copied ? <CheckIcon /> : <ContentCopyIcon />}
            </IconButton>
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={handleShare}
          sx={{
            width: '100%',
            border: '2px dashed',
            borderColor: 'secondary.light',
            borderRadius: 3,
            p: 2,
            cursor: 'pointer',
            textAlign: 'left',
            font: 'inherit',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 2,
            },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" color="primary.main">
              {t('friends.sheet.shareVia')}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {t('friends.sheet.shareViaHint')}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'grid',
              flex: '0 0 auto',
              placeItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <ShareIcon />
          </Box>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default InviteCompanionSheet;
